import { AsyncPipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    NotificationLogFilterModel,
    NotificationLogModel,
    NotificationLogModelListRequestModel,
    NotificationLogModelListResponseModel,
    NotificationsService,
} from 'lemunzo-api';
import { SortMeta } from 'primeng/api';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { catchError, finalize, Observable, of, Subject, switchMap } from 'rxjs';

import { NotificationLogsDialogData } from '../../models/notification-logs-dialog-data';
import { PrimeNGTableColModel } from '../../models/primeng-table-col-model';
import { SharedDialogShellConfig } from '../../models/shared-dialog-shell-config';
import { TranslateDatePipe } from '../../pipes/translate-date.pipe';
import { ErrorService } from '../../services/error.service';
import { PrimengLocaleService } from '../../services/primeng-locale.service';
import { QueryService } from '../../services/query.service';
import { SharedDialogService } from '../../services/shared-dialog.service';
import { TableConfigService } from '../../services/table-config.service';
import { languageChangeSignal } from '../../utils/language-change-signal';
import { NotificationLogDetailDialogComponent } from '../notification-log-detail-dialog/notification-log-detail-dialog.component';
import { PrimeNGNoRecordsComponent } from '../primeng-no-records/primeng-no-records.component';
import { SharedDialogShellComponent } from '../shared-dialog-shell/shared-dialog-shell.component';

/**
 * Self-contained dialog listing the current user's notification logs, newest first,
 * with an "Unread only" toggle. Loads the list itself, opens the detail dialog on row
 * activation and marks unread logs as read. Callers only open it and refresh their
 * badge via `onMarkedAsRead`.
 */
@Component({
    selector: 'apcam-notification-logs-dialog',
    templateUrl: './notification-logs-dialog.component.html',
    styleUrl: './notification-logs-dialog.component.scss',
    imports: [
        AsyncPipe,
        MatSlideToggleModule,
        PrimeNGNoRecordsComponent,
        SharedDialogShellComponent,
        TableModule,
        TagModule,
        TranslateDatePipe,
        TranslatePipe,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationLogsDialogComponent {
    private readonly data = inject<NotificationLogsDialogData>(MAT_DIALOG_DATA);
    private readonly destroyRef = inject(DestroyRef);
    private readonly dialogService = inject(SharedDialogService);
    private readonly errorService = inject(ErrorService);
    private readonly notificationsService = inject(NotificationsService);
    private readonly primengLocaleService = inject(PrimengLocaleService);
    private readonly tableConfigService = inject(TableConfigService);
    private readonly translateService = inject(TranslateService);

    private readonly loadRequest$ =
        new Subject<NotificationLogModelListRequestModel>();

    /** IDs of logs whose detail is being fetched; prevents duplicate GET/PUT on repeated clicks. */
    private readonly openingIds = new Set<number>();

    private readonly currentLang = languageChangeSignal(this.translateService);

    /** Caller's filter combined with the "Unread only" toggle; `undefined` read means all logs. */
    private readonly filter = computed<NotificationLogFilterModel>(() => ({
        ...this.data.filter,
        read: this.unreadOnly() ? false : undefined,
    }));

    /** Last table lazy-load event; replayed (from page 1) when the unread filter changes. */
    protected readonly lastLoadEvent = signal<TableLazyLoadEvent>({});
    protected readonly items = signal<NotificationLogModel[]>([]);
    protected readonly loading = signal(false);
    protected readonly totalCount = signal(0);
    protected readonly unreadOnly = signal(false);

    protected readonly defaultSort: SortMeta[] = [
        { field: 'sentAtUtc', order: -1 },
    ];

    protected readonly tableCurrentPageReportTemplate = computed(() => {
        this.currentLang();
        return this.tableConfigService.getTableCurrentPageReportTemplate();
    });

    protected readonly tableRowDefault = TableConfigService.TableRowDefault;
    protected readonly tableRowsOptions = TableConfigService.TableRowsOptions;

    protected readonly locale$: Observable<string> =
        this.primengLocaleService.getLocale() ?? of('');

    protected readonly columns: PrimeNGTableColModel[] = [
        {
            field: 'sentAtUtc',
            label: 'shared.skeleton.notifications.logsTable.columns.sentAt',
            sort: true,
        },
        {
            field: 'title',
            label: 'shared.skeleton.notifications.logsTable.columns.title',
            sort: false,
        },
        {
            field: 'body',
            label: 'shared.skeleton.notifications.logsTable.columns.message',
            sort: false,
        },
    ];

    protected readonly dialogShellConfig: SharedDialogShellConfig<void> = {
        title: this.translateService.instant(
            'shared.skeleton.notifications.logsDialog.title'
        ),
        cancelLabel: this.translateService.instant('shared.buttons.close'),
        cancelIcon: 'close',
    };

    constructor() {
        this.loadRequest$
            .pipe(
                switchMap((request) => {
                    this.loading.set(true);

                    return this.notificationsService
                        .getMyNotificationLogs(request, this.filter())
                        .pipe(
                            catchError((error) => {
                                this.showError(error);
                                return of<NotificationLogModelListResponseModel>(
                                    { items: [], totalCount: 0 }
                                );
                            }),
                            finalize(() => this.loading.set(false))
                        );
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe((response) => {
                this.items.set(response.items);
                this.totalCount.set(response.totalCount);
            });
    }

    protected loadItems(event: TableLazyLoadEvent): void {
        this.lastLoadEvent.set(event);

        // Sort comes from `defaultSort`; without `removableSort` the table never clears it.
        this.loadRequest$.next(QueryService.getTableListRequestModel(event));
    }

    /** Reloads from the first page, keeping the current sort and page size. */
    protected onUnreadOnlyChange(checked: boolean): void {
        this.unreadOnly.set(checked);
        this.loadItems({ ...this.lastLoadEvent(), first: 0 });
    }

    protected openDetail(item: NotificationLogModel): void {
        if (this.openingIds.has(item.id)) {
            return;
        }

        this.openingIds.add(item.id);

        this.notificationsService
            .getMyNotificationLog(item.id)
            .pipe(
                finalize(() => this.openingIds.delete(item.id)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: (notificationLog) => {
                    this.dialogService.open<
                        NotificationLogDetailDialogComponent,
                        NotificationLogModel
                    >(NotificationLogDetailDialogComponent, {
                        data: notificationLog,
                    });

                    if (!item.read) {
                        this.markAsRead(item.id);
                    }
                },
                error: (error) => this.showError(error),
            });
    }

    private markAsRead(notificationLogId: number): void {
        // No takeUntilDestroyed: closing the dialog must not abort the PUT and leave the log unread.
        this.notificationsService
            .markMyNotificationLogAsRead(notificationLogId)
            .subscribe({
                next: () => {
                    this.items.update((items) =>
                        items.map((item) =>
                            item.id === notificationLogId
                                ? { ...item, read: true }
                                : item
                        )
                    );
                    this.data.onMarkedAsRead?.();
                },
                error: (error) => this.showError(error),
            });
    }

    private showError(error: HttpErrorResponse): void {
        this.errorService.generalToastIgnoringGloballyHandledErrors(
            this.data.errorZone,
            error
        );
    }
}
